import React, { useMemo, useState, useCallback } from 'react';
import { Row, Col, Button, Form, Table, Badge, Modal, ButtonGroup } from 'react-bootstrap';
import numeral from 'numeral';
import axios from 'axios';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { Link } from 'react-router-dom';

import dayjs from '../../../lib/day';
import PrivateLayout from '../../layouts/PrivateLayout';
import Panel from '../../common/Panel';
import { useUser } from '../../../contexts/UserContext';
import {
  useFetchClaimsManagerMetrics,
  useFetchClaimsManagerMetricClaims,
  useFetchClaimsManagerRevenueDetails,
} from '../../../services/adminMetrics';

// Days are Central time (2026-09-30: the page counted in UTC, so after 7 PM
// Central it already thought it was tomorrow, and "This month" flipped early).
const CT = 'America/Chicago';
// Today's Central calendar date, as a plain date (no time zone math after).
const todayCT = () => dayjs.utc(dayjs().tz(CT).format('YYYY-MM-DD'));

const quarterRange = (d) => {
  const month = d.month(); // 0-based
  const qStartMonth = Math.floor(month / 3) * 3;
  const start = d.month(qStartMonth).startOf('month');
  const end = start.add(2, 'month').endOf('month');
  return { start, end };
};

const lastQuarterRange = (d) => {
  const prev = d.subtract(3, 'month');
  return quarterRange(prev);
};

const EXPORT_DETAIL_COLUMNS = [
  { key: 'claimId', label: 'Claim ID' },
  { key: 'veteranName', label: 'Veteran Name' },
  { key: 'claimsManager', label: 'Claims Manager' },
  { key: 'claimsManagerEmail', label: 'Claims Manager Email' },
  { key: 'submittedAt', label: 'Submitted At' },
  { key: 'submissionTypes', label: 'Submission Types' },
  { key: 'estimatedRating', label: 'Estimated Rating' },
  { key: 'currentRating', label: 'Current Rating' },
  { key: 'newVaRating', label: 'New VA Rating' },
  { key: 'claimStatus', label: 'Claim Status' },
];

const ALL_COLUMN_KEYS = EXPORT_DETAIL_COLUMNS.map((c) => c.key);

const ClaimsManagerMetrics = () => {
  const { hasPermission } = useUser();

  // Permission-based access check
  const hasMetricsAccess = hasPermission('reports:claims_metrics');
  const today = todayCT();

  const [filters, setFilters] = useState({
    preset: 'this_month',
    startDate: today.startOf('month').format('YYYY-MM-DD'),
    endDate: today.endOf('month').format('YYYY-MM-DD'),
    search: '',
  });

  const { isLoading, data } = useFetchClaimsManagerMetrics(
    filters.startDate || undefined,
    filters.endDate || undefined,
    { enabled: hasMetricsAccess },
  );

  const totals = data?.totals || null;
  const rows = Array.isArray(data?.rows) ? data.rows : [];

  const filteredRows = useMemo(() => {
    const q = String(filters.search || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = String(r.name || '').toLowerCase();
      const email = String(r.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [rows, filters.search]);

  const [claimsModal, setClaimsModal] = useState(null); // { managerId, managerName, metric }
  const [revenueModal, setRevenueModal] = useState(null); // { managerId, managerName, bucket }

  const claimsModalQuery = useFetchClaimsManagerMetricClaims(
    claimsModal?.managerId,
    claimsModal?.metric,
    filters.startDate || undefined,
    filters.endDate || undefined,
    { enabled: !!claimsModal?.managerId && !!claimsModal?.metric },
  );

  const revenueModalQuery = useFetchClaimsManagerRevenueDetails(
    revenueModal?.managerId,
    revenueModal?.bucket,
    filters.startDate || undefined,
    filters.endDate || undefined,
    { enabled: Boolean(revenueModal && (revenueModal.managerId || revenueModal.bucket)) },
  );

  const setPreset = (preset) => {
    const now = todayCT();
    let startDate = '';
    let endDate = '';

    switch (preset) {
      case 'this_month': {
        startDate = now.startOf('month').format('YYYY-MM-DD');
        endDate = now.endOf('month').format('YYYY-MM-DD');
        break;
      }
      case 'last_month': {
        const last = now.subtract(1, 'month');
        startDate = last.startOf('month').format('YYYY-MM-DD');
        endDate = last.endOf('month').format('YYYY-MM-DD');
        break;
      }
      case 'this_quarter': {
        const { start, end } = quarterRange(now);
        startDate = start.format('YYYY-MM-DD');
        endDate = end.format('YYYY-MM-DD');
        break;
      }
      case 'last_quarter': {
        const { start, end } = lastQuarterRange(now);
        startDate = start.format('YYYY-MM-DD');
        endDate = end.format('YYYY-MM-DD');
        break;
      }
      case 'this_year': {
        startDate = now.startOf('year').format('YYYY-MM-DD');
        endDate = now.endOf('year').format('YYYY-MM-DD');
        break;
      }
      case 'all_time':
      default: {
        startDate = '';
        endDate = '';
        break;
      }
    }

    setFilters((prev) => ({
      ...prev,
      preset,
      startDate,
      endDate,
    }));
  };

  const exportCsv = () => {
    const csvConfig = mkConfig({
      filename: `claims-manager-metrics_${filters.startDate || 'all'}_${filters.endDate || 'time'}`,
      useKeysAsHeaders: true,
    });

    const rowsForCsv = filteredRows.map((r) => ({
      Name: r.name || '',
      Email: r.email || '',
      'Submissions (points)': r.submissionPoints || 0,
      'New/Increase': r.submissions?.new_increase || 0,
      Supplemental: r.submissions?.supplemental || 0,
      'HLR (points)': (r.submissions?.hlr || 0) * 0.25,
      BDD: r.submissions?.bdd || 0,
      'Claims Invoiced': r.claimsInvoiced || 0,
      'Revenue (QuickBooks)': r.revenue || 0,
      'Avg Revenue / Submission Point': r.avgRevenuePerSubmissionPoint || 0,
    }));

    const csv = generateCsv(csvConfig)(rowsForCsv);
    download(csvConfig)(csv);
  };

  // Detail export (claim-level)
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(ALL_COLUMN_KEYS);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportDateMode, setExportDateMode] = useState('all');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');

  const openExportModal = useCallback(() => {
    setSelectedColumns(ALL_COLUMN_KEYS);
    // Initialize date mode from current page filters
    const hasStart = !!filters.startDate;
    const hasEnd = !!filters.endDate;
    if (hasStart && hasEnd) {
      setExportDateMode('between');
      setExportStartDate(filters.startDate);
      setExportEndDate(filters.endDate);
    } else if (hasStart) {
      setExportDateMode('after');
      setExportStartDate(filters.startDate);
      setExportEndDate('');
    } else if (hasEnd) {
      setExportDateMode('before');
      setExportStartDate('');
      setExportEndDate(filters.endDate);
    } else {
      setExportDateMode('all');
      setExportStartDate('');
      setExportEndDate('');
    }
    setShowExportModal(true);
  }, [filters.startDate, filters.endDate]);

  const toggleColumn = (key) => {
    setSelectedColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const getExportDateParams = useCallback(() => {
    switch (exportDateMode) {
      case 'after':
        return { startDate: exportStartDate || undefined };
      case 'before':
        return { endDate: exportEndDate || undefined };
      case 'between':
      case 'this_quarter':
      case 'last_quarter':
        return {
          startDate: exportStartDate || undefined,
          endDate: exportEndDate || undefined,
        };
      default:
        return {};
    }
  }, [exportDateMode, exportStartDate, exportEndDate]);

  const canExport = useMemo(() => {
    if (selectedColumns.length === 0) return false;
    if (exportDateMode === 'after' && !exportStartDate) return false;
    if (exportDateMode === 'before' && !exportEndDate) return false;
    if (exportDateMode === 'between' && (!exportStartDate || !exportEndDate)) return false;
    return true;
  }, [exportDateMode, exportStartDate, exportEndDate, selectedColumns.length]);

  const exportDetailCsv = useCallback(async () => {
    setExporting(true);
    setExportError(null);
    try {
      const dateParams = getExportDateParams();
      const res = await axios.get('/admin/metrics/claims-managers/submissions/export', {
        params: dateParams,
      });
      const exportRows = (res.data?.rows || []).map((r) => {
        const row = {};
        EXPORT_DETAIL_COLUMNS.forEach((col) => {
          if (selectedColumns.includes(col.key)) {
            row[col.label] = r[col.key] != null ? r[col.key] : '';
          }
        });
        return row;
      });
      const filenameParts = ['claims-submissions-detail'];
      if (dateParams.startDate && dateParams.endDate) {
        filenameParts.push(dateParams.startDate, dateParams.endDate);
      } else if (dateParams.startDate) {
        filenameParts.push('after', dateParams.startDate);
      } else if (dateParams.endDate) {
        filenameParts.push('before', dateParams.endDate);
      } else {
        filenameParts.push('all-time');
      }
      const csvConfig = mkConfig({
        filename: filenameParts.join('_'),
        useKeysAsHeaders: true,
      });
      const csv = generateCsv(csvConfig)(exportRows);
      download(csvConfig)(csv);
      setShowExportModal(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Detail export failed:', err);
      setExportError(err.response?.data?.error || 'Failed to export. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [getExportDateParams, selectedColumns]);

  if (!hasMetricsAccess) {
    return (
      <PrivateLayout>
        <Panel>
          <h2>Claims Manager Metrics</h2>
          <div>You do not have access to this page.</div>
        </Panel>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <Row className="mb-4">
        <Col>
          <Panel className="pb-3">
            <Row className="align-items-center mb-4">
              <Col>
                <h2 className="mb-1">Claims Manager Metrics</h2>
                <div className="text-muted small">
                  Range (Submitted to VA — event log): {filters.startDate || 'All time'} →{' '}
                  {filters.endDate || 'All time'}
                </div>
              </Col>
              <Col xs={12} md="auto" className="mt-3 mt-md-0">
                <div className="d-flex gap-2 justify-content-md-end align-items-center">
                  <Form.Control
                    type="search"
                    placeholder="Search by name/email"
                    value={filters.search}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, search: e.target.value }))
                    }
                    style={{ maxWidth: 240 }}
                  />
                  <Button
                    variant="outline-primary"
                    className="text-nowrap"
                    onClick={exportCsv}
                    disabled={!filteredRows.length}
                  >
                    Export CSV
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="text-nowrap"
                    onClick={openExportModal}
                  >
                    Export Detail
                  </Button>
                </div>
              </Col>
            </Row>

            <div className="mb-4">
              <ButtonGroup>
                {[
                  { key: 'this_month', label: 'This Month' },
                  { key: 'last_month', label: 'Last Month' },
                  { key: 'this_quarter', label: 'This Quarter' },
                  { key: 'last_quarter', label: 'Last Quarter' },
                  { key: 'this_year', label: 'This Year' },
                  { key: 'all_time', label: 'All Time' },
                ].map((p) => (
                  <Button
                    key={p.key}
                    size="sm"
                    variant={filters.preset === p.key ? 'primary' : 'outline-secondary'}
                    onClick={() => setPreset(p.key)}
                  >
                    {p.label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>

            <Row className="g-3 align-items-end">
              <Col xs={6} md={3} lg={2}>
                <Form.Label className="small text-muted mb-1">Start</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      preset: 'custom',
                      startDate: e.target.value,
                    }))
                  }
                />
              </Col>
              <Col xs={6} md={3} lg={2}>
                <Form.Label className="small text-muted mb-1">End</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      preset: 'custom',
                      endDate: e.target.value,
                    }))
                  }
                />
              </Col>
              <Col xs={12} md="auto" className="ms-md-auto">
                {data?.quickbooks?.connected === false ? (
                  <Badge bg="danger" className="px-2 py-2">
                    QuickBooks: not connected
                  </Badge>
                ) : (
                  <Badge bg="success" className="px-2 py-2">
                    QuickBooks: connected
                  </Badge>
                )}
              </Col>
            </Row>
          </Panel>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Panel className="pb-3">
            <h4 className="mb-4">Totals</h4>
            {isLoading ? (
              <div>Loading…</div>
            ) : totals ? (
              <Row className="g-3">
                <Col xs={6} lg={2}>
                  <div className="p-3 bg-light border rounded-3 h-100">
                    <div className="text-muted small fw-bold mb-1 text-uppercase">
                      Veterans Submitted
                    </div>
                    <div className="fs-4 fw-bold text-dark">
                      {numeral(totals.veteransSubmitted?.veterans || 0).format(
                        '0,0',
                      )}
                    </div>
                    <div className="text-muted small">
                      across {totals.veteransSubmitted?.claims || 0}{' '}
                      {(totals.veteransSubmitted?.claims || 0) === 1
                        ? 'claim'
                        : 'claims'}
                    </div>
                  </div>
                </Col>
                <Col xs={6} lg={2}>
                  <div className="p-3 bg-light border rounded-3 h-100">
                    <div className="text-muted small fw-bold mb-1 text-uppercase">
                      Submissions
                    </div>
                    <div className="fs-4 fw-bold text-dark">
                      {numeral(totals.submissionPoints || 0).format('0,0.00')}
                    </div>
                    <div className="text-muted small">points</div>
                  </div>
                </Col>
                <Col xs={6} lg={2}>
                  <div className="p-3 bg-light border rounded-3 h-100">
                    <div className="text-muted small fw-bold mb-1 text-uppercase">
                      Invoiced
                    </div>
                    <div className="fs-4 fw-bold text-dark">
                      {numeral(totals.claimsInvoiced || 0).format('0,0')}
                    </div>
                    <div className="text-muted small">claims (from submitted cohort)</div>
                  </div>
                </Col>
                {/* lg widths sum to 12 with the Veterans Submitted tile:
                    2+2+2+3+3. */}
                <Col xs={12} lg={3}>
                  <div className="p-3 bg-light border rounded-3 h-100">
                    <div className="text-muted small fw-bold mb-1 text-uppercase">Revenue</div>
                    <div className="d-flex align-items-baseline gap-2">
                      <div className="fs-4 fw-bold text-dark">
                        {data?.quickbooks?.connected === false
                          ? 'N/A'
                          : `$${numeral(totals.totalRevenue || 0).format('0,0.00')}`}
                      </div>
                      {data?.quickbooks?.connected !== false && (
                        <div className="text-muted small">
                          (Avg: {`$${numeral(totals.avgRevenuePerSubmissionPoint || 0).format('0,0')}`}/pt)
                        </div>
                      )}
                    </div>
                    <div className="text-muted small mt-1">
                      QuickBooks invoices for customers with a Submitted-to-VA event in this range
                    </div>
                  </div>
                </Col>
                <Col xs={12} lg={3}>
                  <div className="p-3 bg-light border rounded-3 h-100">
                    <div className="text-muted small fw-bold mb-2 text-uppercase">
                      Breakdown
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Badge bg="primary" className="fw-normal px-2 py-1">
                        New/Inc: {totals.submissions?.new_increase || 0}
                      </Badge>
                      <Badge bg="info" text="dark" className="fw-normal px-2 py-1">
                        Supp: {totals.submissions?.supplemental || 0}
                      </Badge>
                      <Badge bg="warning" text="dark" className="fw-normal px-2 py-1">
                        HLR (pts):{' '}
                        {numeral((totals.submissions?.hlr || 0) * 0.25).format('0,0.00')}
                      </Badge>
                      <Badge bg="secondary" className="fw-normal px-2 py-1">
                        BDD: {totals.submissions?.bdd || 0}
                      </Badge>
                    </div>
                  </div>
                </Col>
              </Row>
            ) : (
              <div>No data.</div>
            )}
          </Panel>
        </Col>
      </Row>

      <Row>
        <Col>
          <Panel>
            <h4 className="mb-4">By Claims Manager</h4>
            {isLoading ? (
              <div>Loading…</div>
            ) : (
              <Table hover responsive className="align-middle text-nowrap">
                <thead className="table-light">
                  <tr>
                    <th className="py-3">Name</th>
                    <th className="py-3">Email</th>
                    <th className="py-3 text-end">Submissions</th>
                    <th className="py-3 text-center">New/Inc</th>
                    <th className="py-3 text-center">Supp</th>
                    <th className="py-3 text-center">HLR (pts)</th>
                    <th className="py-3 text-center">BDD</th>
                    <th className="py-3 text-center">Invoiced</th>
                    <th className="py-3 text-end">Revenue</th>
                    <th className="py-3 text-end">Avg Rev/Pt</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center text-muted py-4">
                        No rows.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((r) => (
                      <tr key={r.userId || r.name}>
                        <td className="py-2 fw-medium">{r.name}</td>
                        <td className="py-2 text-muted">{r.email}</td>
                        <td className="py-2 text-end">{numeral(r.submissionPoints || 0).format('0,0.00')}</td>
                        <td className="py-2 text-center">{r.submissions?.new_increase || 0}</td>
                        <td className="py-2 text-center">{r.submissions?.supplemental || 0}</td>
                        <td className="py-2 text-center">
                          {numeral((r.submissions?.hlr || 0) * 0.25).format('0,0.00')}
                        </td>
                        <td className="py-2 text-center">{r.submissions?.bdd || 0}</td>
                        <td className="py-2 text-center">
                          {r.userId ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() =>
                                setClaimsModal({
                                  managerId: r.userId,
                                  managerName: r.name,
                                  metric: 'invoiced',
                                })
                              }
                            >
                              {numeral(r.claimsInvoiced || 0).format('0,0')}
                            </Button>
                          ) : (
                            numeral(r.claimsInvoiced || 0).format('0,0')
                          )}
                        </td>
                        <td className="py-2 text-end">
                          {data?.quickbooks?.connected === false
                            ? 'N/A'
                            : r.userId || r.name === 'Unattributed' ? (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0"
                                  onClick={() =>
                                    setRevenueModal({
                                      managerId: r.userId,
                                      managerName: r.name,
                                      bucket: r.userId ? null : 'unattributed',
                                    })
                                  }
                                >
                                  {`$${numeral(r.revenue || 0).format('0,0.00')}`}
                                </Button>
                              ) : (
                                `$${numeral(r.revenue || 0).format('0,0.00')}`
                              )}
                        </td>
                        <td className="py-2 text-end">
                          {data?.quickbooks?.connected === false
                            ? 'N/A'
                            : `$${numeral(r.avgRevenuePerSubmissionPoint || 0).format('0,0.00')}`}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Panel>
        </Col>
      </Row>

      {/* Claims drilldown modal */}
      <Modal
        show={!!claimsModal}
        onHide={() => setClaimsModal(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>
            {claimsModal?.metric === 'submitted' ? 'Claims Submitted' : 'Claims Invoiced'} —{' '}
            {claimsModal?.managerName || ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {claimsModalQuery.isLoading ? (
            <div className="text-center py-4">Loading…</div>
          ) : claimsModalQuery.data && Array.isArray(claimsModalQuery.data.claims) ? (
            <Table hover responsive className="align-middle">
              <thead className="table-light">
                <tr>
                  <th className="py-2">Claim</th>
                  <th className="py-2">Client</th>
                  <th className="py-2">Email</th>
                  <th className="py-2">{claimsModal?.metric === 'submitted' ? 'Submitted At' : 'Invoiced At'}</th>
                </tr>
              </thead>
              <tbody>
                {claimsModalQuery.data.claims.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      No claims.
                    </td>
                  </tr>
                ) : (
                  claimsModalQuery.data.claims.map((c) => (
                    <tr key={c.claimId}>
                      <td className="py-2">
                        <Link to={`/admin/profile/${c.claimId}`} target="_blank" rel="noreferrer">
                          {c.claimId.slice(0, 8)}…
                        </Link>
                      </td>
                      <td className="py-2">
                        {c.userFirstName || ''} {c.userLastName || ''}
                      </td>
                      <td className="py-2 text-muted">{c.userEmail || ''}</td>
                      <td className="py-2">
                        {claimsModal?.metric === 'submitted'
                          ? c.submittedAt
                            ? dayjs.utc(c.submittedAt).tz(CT).format('MM/DD/YYYY h:mm A')
                            : ''
                          : c.invoicedAt
                            ? dayjs.utc(c.invoicedAt).tz(CT).format('MM/DD/YYYY h:mm A')
                            : ''}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          ) : (
            <div className="text-muted text-center py-4">No data.</div>
          )}
        </Modal.Body>
      </Modal>

      {/* Revenue drilldown modal */}
      <Modal
        show={!!revenueModal}
        onHide={() => setRevenueModal(null)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>
            Revenue Breakdown — {revenueModal?.managerName || ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {revenueModalQuery.isLoading ? (
            <div className="text-center py-4">Loading…</div>
          ) : revenueModalQuery.data && revenueModalQuery.data.quickbooks?.connected === false ? (
            <div className="text-danger text-center py-4">QuickBooks is not connected.</div>
          ) : revenueModalQuery.data && Array.isArray(revenueModalQuery.data.groups) ? (
            <>
              <div className="mb-3 d-flex align-items-center gap-3">
                <span className="fs-5 fw-bold">
                  Total: {`$${numeral(revenueModalQuery.data.total || 0).format('0,0.00')}`}
                </span>
                {revenueModalQuery.data.quickbooksInvoiceSearchUrl ? (
                  <a
                    href={revenueModalQuery.data.quickbooksInvoiceSearchUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    Search in QuickBooks
                  </a>
                ) : null}
              </div>

              {revenueModalQuery.data.bucket === 'unattributed' ? (
                <div className="mb-3">
                  <span className="text-muted small me-2">Unattributed reasons:</span>
                  {Object.entries(revenueModalQuery.data.unmatchedReasonCounts || {}).map(
                    ([k, v]) => (
                      <Badge key={k} bg="secondary" className="me-1 px-2 py-1">
                        {k}: {v}
                      </Badge>
                    ),
                  )}
                </div>
              ) : null}

              <Table hover responsive className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="py-2">Client</th>
                    <th className="py-2">Claim</th>
                    <th className="py-2 text-end">Total</th>
                    <th className="py-2">Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {revenueModalQuery.data.groups.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        No revenue lines.
                      </td>
                    </tr>
                  ) : (
                    revenueModalQuery.data.groups.map((g) => (
                      <tr key={`${g.clientUserId || ''}|${g.clientEmail || ''}|${g.clientName || ''}|${g.claimId || ''}`}>
                        <td className="py-2">
                          <div className="fw-medium">{g.clientName}</div>
                          {g.clientEmail ? <div className="text-muted small">{g.clientEmail}</div> : null}
                        </td>
                        <td className="py-2">
                          {g.claimId ? (
                            <Link to={`/admin/profile/${g.claimId}`} target="_blank" rel="noreferrer">
                              {g.claimId.slice(0, 8)}…
                            </Link>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="py-2 text-end fw-medium">{`$${numeral(g.totalAmount || 0).format('0,0.00')}`}</td>
                        <td className="py-2">
                          <ul className="mb-0 list-unstyled">
                            {(g.invoices || []).map((inv, idx) => (
                              <li key={`${inv.invoiceId || inv.docNumber || inv.url || 'inv'}-${idx}`} className="mb-1">
                                {inv.url ? (
                                  <a href={inv.url} target="_blank" rel="noreferrer">
                                    {inv.docNumber ? `#${inv.docNumber}` : `#${inv.invoiceId}`}
                                  </a>
                                ) : (
                                  <span>
                                    {inv.docNumber ? `#${inv.docNumber}` : `#${inv.invoiceId || ''}`}
                                  </span>
                                )}{' '}
                                — {`$${numeral(inv.amount || 0).format('0,0.00')}`}
                                {inv.txnDate ? (
                                  <span className="text-muted small">
                                    {' '}
                                    ({dayjs.utc(inv.txnDate).format('MM/DD/YYYY')})
                                  </span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </>
          ) : (
            <div className="text-muted text-center py-4">No data.</div>
          )}
        </Modal.Body>
      </Modal>

      {/* Detail export column picker modal */}
      <Modal
        show={showExportModal}
        onHide={() => { setShowExportModal(false); setExportError(null); }}
        centered
        aria-labelledby="export-detail-modal-title"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title id="export-detail-modal-title">Export Submissions Detail</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          {exportError && (
            <div className="alert alert-danger mb-3" role="alert">{exportError}</div>
          )}
          <div className="d-flex gap-2 align-items-center border-bottom pb-3 mb-3">
            <Form.Select
              size="sm"
              aria-label="Date range mode"
              value={exportDateMode}
              onChange={(e) => {
                const mode = e.target.value;
                setExportDateMode(mode);
                const now = todayCT();
                if (mode === 'all') {
                  setExportStartDate('');
                  setExportEndDate('');
                } else if (mode === 'this_quarter') {
                  const { start, end } = quarterRange(now);
                  setExportStartDate(start.format('YYYY-MM-DD'));
                  setExportEndDate(end.format('YYYY-MM-DD'));
                } else if (mode === 'last_quarter') {
                  const { start, end } = lastQuarterRange(now);
                  setExportStartDate(start.format('YYYY-MM-DD'));
                  setExportEndDate(end.format('YYYY-MM-DD'));
                }
              }}
              style={{ width: 'auto', minWidth: 140 }}
            >
              <option value="all">All Time</option>
              <option value="this_quarter">This Quarter</option>
              <option value="last_quarter">Last Quarter</option>
              <option value="after">After</option>
              <option value="before">Before</option>
              <option value="between">Between</option>
            </Form.Select>
            {['after', 'between', 'this_quarter', 'last_quarter'].includes(exportDateMode) && (
              <Form.Control
                type="date"
                size="sm"
                aria-label="Start date"
                value={exportStartDate}
                onChange={(e) => setExportStartDate(e.target.value)}
                readOnly={exportDateMode === 'this_quarter' || exportDateMode === 'last_quarter'}
                style={{ width: 'auto' }}
              />
            )}
            {['between', 'this_quarter', 'last_quarter'].includes(exportDateMode) && (
              <span className="text-muted small">to</span>
            )}
            {['before', 'between', 'this_quarter', 'last_quarter'].includes(exportDateMode) && (
              <Form.Control
                type="date"
                size="sm"
                aria-label="End date"
                value={exportEndDate}
                onChange={(e) => setExportEndDate(e.target.value)}
                readOnly={exportDateMode === 'this_quarter' || exportDateMode === 'last_quarter'}
                style={{ width: 'auto' }}
              />
            )}
          </div>

          <p className="text-muted small mb-3">
            Select which columns to include in the CSV export.
          </p>
          <div className="d-flex gap-3 mb-3">
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => setSelectedColumns(ALL_COLUMN_KEYS)}
            >
              Select All
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => setSelectedColumns([])}
            >
              Deselect All
            </Button>
          </div>
          {EXPORT_DETAIL_COLUMNS.map((col) => (
            <Form.Check
              key={col.key}
              type="checkbox"
              id={`export-col-${col.key}`}
              label={col.label}
              checked={selectedColumns.includes(col.key)}
              onChange={() => toggleColumn(col.key)}
              className="mb-2"
            />
          ))}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={exportDetailCsv}
            disabled={exporting || !canExport}
            title={!canExport ? 'Select at least one column and fill in required dates' : undefined}
          >
            {exporting ? 'Exporting…' : 'Download CSV'}
          </Button>
        </Modal.Footer>
      </Modal>
    </PrivateLayout>
  );
};

export default ClaimsManagerMetrics;


